/**
 * Per ADR-0052. Pure helpers for extracting and recombining proposal
 * anchors in an assistant message. Lives outside the Vue component so
 * it's trivially unit-testable without spinning up a DOM environment.
 *
 * The anchor syntax is intentionally narrow:
 *
 *   [Label text](#proposal-<proposalId>)
 *
 * Labels are everything between the brackets, no embedded newline.
 * `proposalId` is alphanumeric + dash + underscore — wider than the
 * 24-hex format `cryptoRandomId()` produces today, but still narrow
 * enough that a stray `#proposal-` mention in prose can't accidentally
 * be mistaken for an anchor.
 */

export interface AnchorPlaceholder {
  proposalId: string;
  label: string;
}

const ANCHOR_REGEX = /\[([^\]\n]+)\]\(#proposal-([A-Za-z0-9_-]+)\)/g;

/** Sentinel that survives the marked + DOMPurify pass. The renderer
 *  splits the sanitised HTML on this pattern and re-inserts a Vue
 *  `ActionChip` at the matching index. */
export const CHIP_SENTINEL_PREFIX = 'CHIP';
export const CHIP_SENTINEL_SUFFIX = '/CHIP';
export const CHIP_SENTINEL_SPLIT = /CHIP(\d+)\/CHIP/;

export function extractAnchors(source: string): {
  processed: string;
  chips: AnchorPlaceholder[];
} {
  const chips: AnchorPlaceholder[] = [];
  const processed = source.replace(ANCHOR_REGEX, (_match, label: string, proposalId: string) => {
    const idx = chips.length;
    chips.push({ proposalId, label });
    return `${CHIP_SENTINEL_PREFIX}${idx}${CHIP_SENTINEL_SUFFIX}`;
  });
  return { processed, chips };
}
